import React, { useState, useEffect } from 'react';
import { listCSVFiles, getCSVDownloadURL, downloadCSVsAsZip } from '../services/api';
import { format } from 'date-fns';

const CSVManagement = () => {
  const [csvFiles, setCSVFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateFilter, setDateFilter] = useState('');
  const [downloading, setDownloading] = useState({});
  const [downloadingZip, setDownloadingZip] = useState({});

  useEffect(() => {
    loadCSVFiles();
  }, [dateFilter]);

  const loadCSVFiles = async () => {
    try {
      setLoading(true);
      const params = dateFilter ? { date: dateFilter } : {};
      const response = await listCSVFiles(params);
      setCSVFiles(response.data || []);
    } catch (error) {
      console.error('Error loading CSV files:', error);
      alert('Failed to load CSV files');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (csvFile) => {
    try {
      setDownloading({ ...downloading, [csvFile.key]: true });
      
      const response = await getCSVDownloadURL(csvFile.key);
      
      // Open download URL in new tab
      window.open(response.download_url, '_blank');
      
    } catch (error) {
      console.error('Error downloading CSV:', error);
      alert('Failed to generate download link');
    } finally {
      setDownloading({ ...downloading, [csvFile.key]: false });
    }
  };

  const handleDownloadZip = async (date) => {
    try {
      setDownloadingZip({ ...downloadingZip, [date]: true });
      
      // Download ZIP blob
      const blob = await downloadCSVsAsZip(date);
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `refi_csvs_${date}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      alert(`✅ ZIP file downloaded successfully!\n\nFile: refi_csvs_${date}.zip`);
      
    } catch (error) {
      console.error('Error downloading ZIP:', error);
      alert('Failed to download ZIP file');
    } finally {
      setDownloadingZip({ ...downloadingZip, [date]: false });
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), 'MMM dd, yyyy HH:mm');
    } catch {
      return dateString;
    }
  };

  const getCategoryFromFilename = (filename) => {
    if (filename.includes('credits_initial')) return 'Initial Credit';
    if (filename.includes('rol_reversal')) return 'ROL Reversal';
    if (filename.includes('overpayment')) return 'Overpayment Adj.';
    if (filename.includes('underpayment')) return 'Underpayment Adj.';
    if (filename.includes('credits_final')) return 'Final Credit';
    if (filename.includes('charges_final')) return 'Final Charge';
    if (filename.includes('advancements_final')) return 'Final Advancement';
    return 'Other';
  };

  const getCategoryColor = (category) => {
    const colors = {
      'Initial Credit': '#4caf50',
      'ROL Reversal': '#ff9800',
      'Overpayment Adj.': '#00bcd4',
      'Underpayment Adj.': '#e91e63',
      'Final Credit': '#2196f3',
      'Final Charge': '#f44336',
      'Final Advancement': '#9c27b0',
      'Other': '#757575',
    };
    return colors[category] || colors['Other'];
  };

  // Group CSVs by date
  const groupedCSVs = csvFiles.reduce((acc, csv) => {
    const date = csv.date || 'Unknown';
    if (!acc[date]) acc[date] = [];
    acc[date].push(csv);
    return acc;
  }, {});

  return (
    <div className="card">
      <div style={styles.header}>
        <h2>CSV Management</h2>
        
        {/* Date Filter */}
        <div style={styles.filters}>
          <input
            type="date"
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
            style={styles.dateInput}
          />
          <button className="button button-secondary" onClick={() => setDateFilter('')}>
            Clear Filter
          </button>
          <button className="button button-primary" onClick={loadCSVFiles}>
            Refresh
          </button>
        </div>
      </div>

      {loading ? (
        <div className="loading">
          <div className="spinner"></div>
        </div>
      ) : csvFiles.length === 0 ? (
        <div style={styles.emptyState}>
          <p>No CSV files found</p>
          {dateFilter && <p style={styles.hint}>Try removing the date filter</p>}
        </div>
      ) : (
        <div>
          {Object.keys(groupedCSVs).sort().reverse().map(date => (
            <div key={date} style={styles.dateGroup}>
              <div style={styles.dateHeaderRow}>
                <h3 style={styles.dateHeader}>{date}</h3>
                <button
                  className="button button-primary"
                  style={styles.zipButton}
                  onClick={() => handleDownloadZip(date)}
                  disabled={downloadingZip[date]}
                >
                  {downloadingZip[date] ? '⏳ Creating ZIP...' : '📦 Download All as ZIP'}
                </button>
              </div>
              
              <div style={styles.tableContainer}>
                <table className="table">
                  <thead>
                    <tr>
                      <th>Filename</th>
                      <th>Category</th>
                      <th>Size</th>
                      <th>Last Modified</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {groupedCSVs[date].map((csv, index) => {
                      const category = getCategoryFromFilename(csv.filename);
                      return (
                        <tr key={index}>
                          <td style={styles.filename}>
                            <code>{csv.filename}</code>
                          </td>
                          <td>
                            <span
                              style={{
                                ...styles.categoryBadge,
                                backgroundColor: getCategoryColor(category),
                              }}
                            >
                              {category}
                            </span>
                          </td>
                          <td>{formatFileSize(csv.size)}</td>
                          <td>{formatDate(csv.last_modified)}</td>
                          <td>
                            <button
                              className="button button-primary"
                              style={styles.downloadButton}
                              onClick={() => handleDownload(csv)}
                              disabled={downloading[csv.key]}
                            >
                              {downloading[csv.key] ? 'Loading...' : '⬇️ Download'}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Legend */}
      <div style={styles.legend}>
        <h4 style={styles.legendTitle}>CSV Categories:</h4>
        <div style={styles.legendItems}>
          {['Initial Credit', 'ROL Reversal', 'Overpayment Adj.', 'Underpayment Adj.', 'Final Credit', 'Final Charge', 'Final Advancement'].map(category => (
            <div key={category} style={styles.legendItem}>
              <span
                style={{
                  ...styles.legendColor,
                  backgroundColor: getCategoryColor(category),
                }}
              ></span>
              <span>{category}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const styles = {
  header: {
    marginBottom: '20px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '15px',
  },
  filters: {
    display: 'flex',
    gap: '10px',
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  dateInput: {
    padding: '8px 12px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    fontSize: '14px',
  },
  emptyState: {
    textAlign: 'center',
    padding: '40px',
    color: '#666',
  },
  hint: {
    fontSize: '14px',
    marginTop: '10px',
  },
  dateGroup: {
    marginBottom: '30px',
  },
  dateHeaderRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '15px',
    paddingBottom: '10px',
    borderBottom: '2px solid #1976d2',
  },
  dateHeader: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#333',
    margin: 0,
  },
  zipButton: {
    padding: '8px 16px',
    fontSize: '14px',
    fontWeight: '600',
    backgroundColor: '#1976d2',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    transition: 'all 0.3s ease',
  },
  tableContainer: {
    overflowX: 'auto',
  },
  filename: {
    fontFamily: 'monospace',
    fontSize: '12px',
  },
  categoryBadge: {
    display: 'inline-block',
    padding: '4px 8px',
    borderRadius: '4px',
    fontSize: '11px',
    fontWeight: '500',
    color: 'white',
  },
  downloadButton: {
    padding: '6px 12px',
    fontSize: '12px',
  },
  legend: {
    marginTop: '30px',
    padding: '15px',
    backgroundColor: '#f5f5f5',
    borderRadius: '4px',
  },
  legendTitle: {
    fontSize: '14px',
    fontWeight: '600',
    marginBottom: '10px',
  },
  legendItems: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '15px',
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '12px',
  },
  legendColor: {
    width: '16px',
    height: '16px',
    borderRadius: '3px',
  },
};

export default CSVManagement;

